import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useDataCache } from '../contexts/BreakdownDataCacheContext';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { getScenesByBreakdownId, getBreakdownById } from '../api/breakdown';
import { getElementsByBreakdownId, getElementRelationsByBreakdownID } from '../api/element';
import { getSchedulesByBreakdownId, getScheduleById, getSlotsByScheduleId, getCalDatesByScheduleId } from '../api/schedule';
import { SceneWithUserNames } from '../types/scene.types';
import { ElementInfo, ElementSceneInfo } from '../types/element.types';
import { ScheduleInfo } from '../types/schedule.types';
import { BreakdownInfo } from '../pages/Home/types';
import type { ScheduleDataCache } from '../types/cache.types';
import { resolveEffectiveScheduleId } from '../utils/resolveUserLastUsed';

export interface UseBreakdownDataLoaderOptions {
  breakdownId: string;
  forceRefresh?: boolean;
  preferredScheduleId?: string | null;
}

/**
 * 分场页统一数据加载：写入 BreakdownDataCache / ScheduleDataCache（含当前日程的 Slot / CalDate）
 */
export function useBreakdownDataLoader(options: UseBreakdownDataLoaderOptions) {
  const { breakdownId, forceRefresh = false, preferredScheduleId = null } = options;

  const { serverSyncedData, stripServerSyncedUserLastUsedScheduleByScheduleId } = useCurrentUser();

  const dataCache = useDataCache();
  const dataCacheRef = useRef(dataCache);
  dataCacheRef.current = dataCache;

  const serverSyncedRef = useRef(serverSyncedData);
  serverSyncedRef.current = serverSyncedData;

  const stripScheduleByIdRef = useRef(stripServerSyncedUserLastUsedScheduleByScheduleId);
  stripScheduleByIdRef.current = stripServerSyncedUserLastUsedScheduleByScheduleId;

  const [breakdownInfo, setBreakdownInfoState] = useState<BreakdownInfo | null>(null);
  const [scenes, setScenes] = useState<SceneWithUserNames[]>([]);
  const [elements, setElements] = useState<ElementInfo[]>([]);
  const [elementRelations, setElementRelations] = useState<ElementSceneInfo[]>([]);
  const [schedules, setSchedules] = useState<ScheduleInfo[]>([]);
  /** 与 ScheduleDataCache.currentScheduleId 一致，供容器传入 useScheduleDataLoader 的 scheduleId */
  const [currentScheduleId, setCurrentScheduleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isScheduleSliceComplete = useCallback(
    (scheduleSlice: ScheduleDataCache | null) => {
      if (!scheduleSlice) return false;
      const list = scheduleSlice.schedules || [];
      if (list.length === 0) return !preferredScheduleId;
      if (preferredScheduleId && scheduleSlice.currentScheduleId !== preferredScheduleId) return false;
      return (
        !!scheduleSlice.currentScheduleId &&
        Array.isArray(scheduleSlice.currentSlots) &&
        Array.isArray(scheduleSlice.currentCalDates)
      );
    },
    [preferredScheduleId]
  );

  const loadFromCache = useCallback(() => {
    const {
      getBreakdownCache: getBd,
      getElementCache: getEl,
      getElementRelationCache: getRel,
      getScheduleCache: getSched,
      setSceneCache: patchScene,
    } = dataCacheRef.current;

    const bdCache = getBd(breakdownId);
    const elementCache = getEl(breakdownId);
    const elementRelationCache = getRel(breakdownId);
    const scheduleSlice = getSched(breakdownId);

    if (bdCache) {
      setBreakdownInfoState(bdCache.breakdownInfo);
      setScenes(bdCache.scenes);

      if (bdCache.scenes && Array.isArray(bdCache.scenes)) {
        bdCache.scenes.forEach((scene: SceneWithUserNames) => {
          patchScene(scene.sceneID, {
            sceneInfo: scene,
          });
        });
      }
    }

    if (elementCache) {
      setElements(elementCache.elements);
    }

    if (elementRelationCache) {
      setElementRelations(elementRelationCache.elementRelations);
    }

    if (scheduleSlice) {
      setSchedules(scheduleSlice.schedules);
      setCurrentScheduleId(scheduleSlice.currentScheduleId ?? null);
    }

    return {
      hasBreakdownCache: !!bdCache,
      hasElementCache: !!elementCache,
      hasElementRelationCache: !!elementRelationCache,
      hasScheduleCacheSlice: !!scheduleSlice && isScheduleSliceComplete(scheduleSlice),
    };
  }, [breakdownId, isScheduleSliceComplete]);

  const loadFromAPI = useCallback(async () => {
    const {
      setBreakdownCache: patchBreakdown,
      setElementCache: patchElements,
      setElementRelationCache: patchRelations,
      setScheduleCache: patchSchedule,
      setSceneCache: patchScene,
      setSlotCache: patchSlot,
      setCalDateCache: patchCalDate,
    } = dataCacheRef.current;

    try {
      setLoading(true);
      setError(null);

      const [breakdownInfoRes, scenesResponse, elementsResponse, relationsResponse, schedulesResponse] =
        await Promise.all([
          getBreakdownById(breakdownId),
          getScenesByBreakdownId(breakdownId),
          getElementsByBreakdownId(breakdownId),
          getElementRelationsByBreakdownID(breakdownId),
          getSchedulesByBreakdownId(breakdownId),
        ]);

      const loadedBreakdownInfo = breakdownInfoRes.data;
      const loadedScenes = scenesResponse.data || [];
      const loadedElements = elementsResponse.data || [];
      const loadedRelations = relationsResponse.data || [];
      const loadedSchedulesList = schedulesResponse.data || [];

      setBreakdownInfoState(loadedBreakdownInfo);
      setScenes(loadedScenes);
      setElements(loadedElements);
      setElementRelations(loadedRelations);
      setSchedules(loadedSchedulesList);

      patchBreakdown(breakdownId, {
        breakdownInfo: loadedBreakdownInfo,
        scenes: loadedScenes,
        elements: loadedElements,
        elementRelations: loadedRelations,
      });

      patchElements(breakdownId, {
        elements: loadedElements,
      });

      patchRelations(breakdownId, {
        elementRelations: loadedRelations,
      });

      loadedScenes.forEach((scene: SceneWithUserNames) => {
        patchScene(scene.sceneID, {
          sceneInfo: scene,
        });
      });

      const effectiveScheduleId =
        (preferredScheduleId &&
        loadedSchedulesList.some((schedule) => schedule.scheduleID === preferredScheduleId)
          ? preferredScheduleId
          : null) ??
        resolveEffectiveScheduleId(
          serverSyncedRef.current.userLastUsedScheduleID,
          breakdownId,
          loadedSchedulesList
        );

      if (effectiveScheduleId) {
        try {
          const [scheduleDetailRes, slotsRes, calDatesRes] = await Promise.all([
            getScheduleById(effectiveScheduleId),
            getSlotsByScheduleId(effectiveScheduleId),
            getCalDatesByScheduleId(effectiveScheduleId),
          ]);

          const scheduleDetail = scheduleDetailRes.data;
          const slots = slotsRes.data || [];
          const calDates = calDatesRes.data || [];

          patchSchedule(breakdownId, {
            schedules: loadedSchedulesList,
            currentScheduleId: effectiveScheduleId,
            currentScheduleInfo: scheduleDetail,
            currentSlots: slots,
            currentCalDates: calDates,
          });

          patchSlot(effectiveScheduleId, {
            scheduleInfo: scheduleDetail,
            slots,
          });

          patchCalDate(effectiveScheduleId, {
            calDates,
          });
          setCurrentScheduleId(effectiveScheduleId);
        } catch (detailErr) {
          if (axios.isAxiosError(detailErr) && detailErr.response?.status === 404) {
            stripScheduleByIdRef.current(effectiveScheduleId);
            patchSchedule(breakdownId, {
              schedules: loadedSchedulesList,
              currentScheduleId: null,
              currentScheduleInfo: null,
              currentSlots: [],
              currentCalDates: [],
            });
            setCurrentScheduleId(null);
          } else {
            throw detailErr;
          }
        }
      } else {
        patchSchedule(breakdownId, {
          schedules: loadedSchedulesList,
          currentScheduleId: null,
          currentScheduleInfo: null,
          currentSlots: [],
          currentCalDates: [],
        });
        setCurrentScheduleId(null);
      }
    } catch (err) {
      console.error('[useBreakdownDataLoader] 数据加载失败:', err);
      setError(err instanceof Error ? err.message : '数据加载失败');
    } finally {
      setLoading(false);
    }
  }, [breakdownId, preferredScheduleId]);

  const refresh = useCallback(async () => {
    await loadFromAPI();
  }, [loadFromAPI]);

  useEffect(() => {
    if (!breakdownId) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      if (forceRefresh) {
        await loadFromAPI();
        return;
      }

      const cacheStatus = loadFromCache();

      if (
        cacheStatus.hasBreakdownCache &&
        cacheStatus.hasElementCache &&
        cacheStatus.hasElementRelationCache &&
        cacheStatus.hasScheduleCacheSlice
      ) {
        setLoading(false);
        return;
      }

      await loadFromAPI();
    };

    loadData();
  }, [breakdownId, forceRefresh, loadFromCache, loadFromAPI]);

  return {
    breakdownInfo,
    scenes,
    elements,
    elementRelations,
    schedules,
    currentScheduleId,
    loading,
    error,
    refresh,
  };
}
